import { useState } from 'react';

import { Modal, useModal } from '@components/modal';
import { useScopedTranslate } from '@locale/translate';

import { useCompanyTravellerAttributeTemplates } from 'api/attributes/hooks';
import {
  useBulkCreateCompanyTraveller,
  useBulkCreateCompanyTravellerTemplate,
} from 'api/company-traveller/hooks';
import { ImportColumn } from 'api/types';

import {
  GettingStarted,
  Import,
  Importing,
  Instruction,
  MatchColumns,
  matchDefaultHeaders,
  MutationErrorType,
  StepType,
  UploadFile,
} from './common';

import { companyTravellerAttributePrefix } from '../../helpers';

export const useBulkImportCompanyTravellersModal = () =>
  useModal(BulkImportCompanyTravellersModal, { variant: 'LARGE' });

export const BulkImportCompanyTravellersModal = () => {
  const { t } = useScopedTranslate(locale);
  const templateMutation = useBulkCreateCompanyTravellerTemplate();
  const companyTravellerAttributeTemplatesQuery = useCompanyTravellerAttributeTemplates();
  const companyTravellerAttributes = companyTravellerAttributeTemplatesQuery.data ?? [];
  const [step, setStep] = useState<StepType>('instruction');
  const [headers, setHeaders] = useState([] as string[]);
  const [data, setData] = useState([] as { [key: string]: string }[]);
  const [mutationErrors, setMutationErrors] = useState<MutationErrorType>({});
  const bulkCreateMutation = useBulkCreateCompanyTraveller(setMutationErrors);

  const baseItems: ImportColumn[] = [
    {
      id: 'name',
      label: t('core.fullName'),
      defaultHeader: 'Full name',
      value: '',
      required: true,
      type: 'text',
    },
    {
      id: 'email',
      label: t('core.email'),
      defaultHeader: 'Email',
      value: '',
      required: true,
      type: 'email',
    },
    {
      id: 'phone',
      label: t('core.phone'),
      defaultHeader: 'Mobile number',
      value: '',
      required: false,
      type: 'phone',
    },
  ];

  const filteredCompanyTravellerAttributes = companyTravellerAttributes.filter(
    (att) => att.attributeType !== 'file' && att.attributeType !== 'file_multi'
  );

  const companyTravellerAttributeItems = filteredCompanyTravellerAttributes.map((attr) => {
    return {
      id: `${companyTravellerAttributePrefix}${attr.shortId}`,
      defaultHeader: attr.templateTitle,
      label: attr.templateTitle,
      required: attr.isRequired,
      type: attr.attributeType,
      value: '',
    } as ImportColumn;
  });

  const items = [...baseItems, ...companyTravellerAttributeItems];
  const [columns, setColumns] = useState<ImportColumn[]>(items);

  const updateColumnValue = (headerList: string[]) => {
    setColumns(matchDefaultHeaders(columns, headerList));
  };

  return (
    <Modal>
      <Modal.SimpleHeader
        title={
          step === 'instruction'
            ? t('importTravellers')
            : step === 'importing'
            ? t('importing')
            : t('csvImportTool')
        }
      />
      <Modal.Content>
        {step === 'instruction' && (
          <Instruction
            setStep={setStep}
            download={() => templateMutation.mutate()}
            templateName={t('templateName', { name: t('traveller') })}
          />
        )}
        {step === 'gettingStarted' && (
          <GettingStarted attributes={companyTravellerAttributes} setStep={setStep} />
        )}
        {step === 'uploadFile' && (
          <UploadFile
            setStep={setStep}
            setHeaders={(value) => {
              setHeaders(value);
              updateColumnValue(value);
            }}
            setData={setData}
          />
        )}
        {step === 'matchColumns' && (
          <MatchColumns
            setStep={setStep}
            headers={headers}
            columns={columns}
            setColumns={setColumns}
          />
        )}
        {step === 'import' && (
          <Import
            columns={columns}
            data={data}
            mutationErrors={mutationErrors}
            isLoading={bulkCreateMutation.isLoading}
            setStep={(value) => {
              setStep(value);
              setMutationErrors({});
            }}
            create={(data) => bulkCreateMutation.mutateAsync(data)}
          />
        )}
        {step === 'importing' && <Importing />}
      </Modal.Content>
    </Modal>
  );
};

export const locale = {
  en: {
    importTravellers: 'Import travellers',
    importing: 'Importing',
    csvImportTool: 'CSV import tool',
    templateName: '{name} - template.csv',
    traveller: 'Traveller',
  },
};
